import type { ChannelIndicatorResp } from "@/api/diagnostics";
import { formatNumber } from "@/utils";
import {
  GROUP_ORDER,
  channelTitle,
  renderGroup,
  type ChannelStructureIndex,
  type ParquetChannelGroupId,
} from "./parquetChannels";

/**
 * 타이밍 보기의 순수 모델. 진단 상세 `channels[]` 를 표 행으로 바꾼다.
 *
 * 값은 모두 **에피소드 자체의 사실**이다(컬렉션 밴드와 무관). 그래서 백분위나 강조 없이
 * 기록된 시간축의 절대값을 그대로 낸다.
 *
 * Owns:
 * - 채널 기록 행(샘플 수·주기·간격 흔들림·빈틈·순서 결함·비유한값·시간 범위) 포맷.
 * - 행 순서. 먼저 그룹(Observation → Action → Other, `GROUP_ORDER`), 그다음 표시 이름 순이다.
 *
 * Does not own:
 * - 채널 이름·그룹 판정. 데이터셋 구조 채널(`parquetChannels`)이 SoT 다.
 */

export type TimingRow = {
  // `<topic>::<name>` 전체 이름. 행 키로 쓴다.
  channel: string;
  title: string;
  group: ParquetChannelGroupId;
  rows: string;
  // 행 간격 중앙값으로 계산한 기록 주기. 간격을 모르면 "—".
  rate: string;
  // 행 간격 흔들림(중앙 절대 편차 / 중앙값).
  jitter: string;
  // 간격이 주기의 5배를 넘은 자리의 비율.
  gap: string;
  // 역행·중복 timestamp 비율. 둘 다 0 이면 "없음".
  order: string;
  nonFinite: string;
  span: string;
};

/** 채널 기록 행. 그룹 순서로 놓고, 같은 그룹 안에서는 표시 이름 순으로 놓는다. */
export function timingRows(
  channels: readonly ChannelIndicatorResp[],
  index: ChannelStructureIndex,
): TimingRow[] {
  return sortByGroup(
    channels.map((channel) => ({
      channel: channel.channel,
      title: channelTitle(channel.channel, index.get(channel.channel)),
      group: renderGroup(index.get(channel.channel), channel.channel),
      rows: formatNumber(channel.n_rows),
      rate: formatRate(channel.dt_median_seconds),
      jitter: channel.dt_jitter_mad_ratio === null ? "—" : formatShare(channel.dt_jitter_mad_ratio),
      gap: formatShare(channel.gap_fraction),
      order: formatOrder(channel.non_monotonic_fraction, channel.duplicate_fraction),
      nonFinite: formatShare(channel.non_finite_fraction),
      span: formatSpan(channel.t_min, channel.t_max),
    })),
  );
}

function sortByGroup<T extends { group: ParquetChannelGroupId; title: string }>(rows: T[]): T[] {
  return rows.sort(
    (a, b) =>
      GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group) ||
      a.title.localeCompare(b.title, "ko-KR"),
  );
}

// 주기를 Hz 로 바꾼다. 간격 중앙값이 없거나 0 이면 주기를 정할 수 없다.
function formatRate(dtMedianSeconds: number | null): string {
  if (dtMedianSeconds === null || !(dtMedianSeconds > 0)) {
    return "—";
  }
  return `${Math.round(1 / dtMedianSeconds)} Hz`;
}

/**
 * 비율을 백분율로 바꾼다.
 * - 0 은 "0%" 다.
 * - 0 은 아니지만 0.1% 미만이면 "0.1% 미만" 이다.
 * 반올림으로 0% 가 되면 "한 번도 없었다" 와 구분되지 않는다.
 */
export function formatShare(fraction: number): string {
  if (fraction === 0) {
    return "0%";
  }
  if (fraction < 0.001) {
    return "0.1% 미만";
  }
  return `${(fraction * 100).toFixed(1)}%`;
}

function formatOrder(nonMonotonic: number, duplicate: number): string {
  if (nonMonotonic === 0 && duplicate === 0) {
    return "없음";
  }
  return `역행 ${formatShare(nonMonotonic)} · 중복 ${formatShare(duplicate)}`;
}

// 에피소드 기준 초라서 음수일 수 있다. 기록이 영상보다 먼저 시작했다는 사실을 유지한다.
function formatSpan(tMin: number | null, tMax: number | null): string {
  if (tMin === null || tMax === null) {
    return "—";
  }
  return `${tMin.toFixed(2)}–${tMax.toFixed(2)}초`;
}
